// import { requestNotificationPermission } from '../firebase';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  ChevronRight,
  Crown,
  Eye,
  EyeOff,
  HardHat,
  ShieldCheck,
  Sparkles,
  Wrench,
} from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { requestPushPermission } from '../services/pushNotifications';
import ProfilePhotoBadge from '../components/ProfilePhotoBadge';
import { clearProfilePhoto, commitPendingProfilePhoto, loadProfilePhoto, PHOTO_REMOVED_FLAG_OWNER, saveProfilePhoto, syncUserProfilePhoto } from '../utils/profilePhotoStorage';
import { dataUrlToFile, uploadUserProfilePhoto } from '../services/profilePhotoApi';

const explainApiError = (err, apiBaseUrl, fallback) => {
  const msg = String(err?.message || '').toLowerCase();
  if (msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('network request failed')) {
    return `Cannot reach server at ${apiBaseUrl}. Please start backend API and try again.`;
  }
  return fallback;
};

const ROLES = [
  { id: 'client', labelKey: 'clientRole', descKey: 'loginRoleClientDesc', icon: HardHat },
  { id: 'owner', labelKey: 'ownerRole', descKey: 'loginRoleOwnerDesc', icon: Building2 },
  { id: 'operator', labelKey: 'operatorRole', descKey: 'loginRoleOperatorDesc', icon: Wrench },
  { id: 'admin', labelKey: 'adminRole', descKey: 'loginRoleAdminDesc', icon: Crown },
];

const TRUST_STATS_BASE = [
  { id: 'machines', valueKey: 'machines', labelKey: 'trustStatMachines' },
  { id: 'owners', valueKey: 'owners', labelKey: 'trustStatOwners', icon: Building2 },
  { id: 'clients', valueKey: 'clients', labelKey: 'trustStatClients', icon: HardHat },
  { id: 'gst', value: 'GST', labelKey: 'trustStatCompliant' },
];

const PORTAL_META = {
  client: {
    titleKey: 'loginPortalClientTitle',
    subtitleKey: 'loginPortalClientSubtitle',
    registerTo: '/register/client',
  },
  owner: {
    titleKey: 'loginPortalOwnerTitle',
    subtitleKey: 'loginPortalOwnerSubtitle',
    registerTo: '/register/owner',
  },
  operator: {
    titleKey: 'loginPortalOperatorTitle',
    subtitleKey: 'loginPortalOperatorSubtitle',
    registerTo: '/register',
  },
  admin: {
    titleKey: 'loginPortalAdminTitle',
    subtitleKey: 'loginPortalAdminSubtitle',
    registerTo: '/register',
  },
};

const Login = ({ fixedPortal = null }) => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const portalRole = fixedPortal && ROLES.some((r) => r.id === fixedPortal) ? fixedPortal : null;
  const photoRole = portalRole
    || (window.location.pathname.includes('owner') ? 'owner'
      : window.location.pathname.includes('operator') ? 'operator'
      : 'client');
  const [step, setStep] = useState(portalRole ? 2 : 1);
  const [role, setRole] = useState(portalRole || 'client');
  const [profilePhoto, setProfilePhoto] = useState('');
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoError, setProfilePhotoError] = useState('');
  const [adminProfilePhoto, setAdminProfilePhoto] = useState('');
  const [adminProfilePhotoFile, setAdminProfilePhotoFile] = useState(null);
  const [adminProfilePhotoError, setAdminProfilePhotoError] = useState('');
  const [loginMode, setLoginMode] = useState('password');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpStep, setOtpStep] = useState('request');
  const [otpLoading, setOtpLoading] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [totpPending, setTotpPending] = useState(false);
  const [totpTempToken, setTotpTempToken] = useState('');
  const [totpToken, setTotpToken] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [machineCountLabel, setMachineCountLabel] = useState('…');
  const [ownerCountLabel, setOwnerCountLabel] = useState('…');
  const [clientCountLabel, setClientCountLabel] = useState('…');
  const loginUsesPhone = role !== 'admin';
  const selectedRole = ROLES.find((r) => r.id === role);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/public/machines/count`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        if (cancelled) return;
        const n = Number(data?.count);
        setMachineCountLabel(Number.isFinite(n) && n >= 0 ? String(n) : '—');
      })
      .catch(() => {
        if (!cancelled) setMachineCountLabel('—');
      });
    return () => { cancelled = true; };
  }, [API_BASE_URL]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/public/owners/count`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        if (cancelled) return;
        const n = Number(data?.count);
        setOwnerCountLabel(Number.isFinite(n) && n >= 0 ? String(n) : '—');
      })
      .catch(() => {
        if (!cancelled) setOwnerCountLabel('—');
      });
    return () => { cancelled = true; };
  }, [API_BASE_URL]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/public/clients/count`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        if (cancelled) return;
        const n = Number(data?.count);
        setClientCountLabel(Number.isFinite(n) && n >= 0 ? String(n) : '—');
      })
      .catch(() => {
        if (!cancelled) setClientCountLabel('—');
      });
    return () => { cancelled = true; };
  }, [API_BASE_URL]);

  const trustStats = TRUST_STATS_BASE.map((stat) => {
    if (stat.valueKey === 'machines') return { ...stat, value: machineCountLabel };
    if (stat.valueKey === 'owners') return { ...stat, value: ownerCountLabel };
    if (stat.valueKey === 'clients') return { ...stat, value: clientCountLabel };
    return stat;
  });

  useEffect(() => {
    if (portalRole) {
      setRole(portalRole);
      setStep(2);
      return;
    }
    const queryRole = searchParams.get('role');
    if (queryRole && ROLES.some((r) => r.id === queryRole)) {
      setRole(queryRole);
      setStep(2);
    }
    if (searchParams.get('mode') === 'otp') {
      setStep(2);
      setLoginMode('otp');
    }
  }, [searchParams, portalRole]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('developmentexpress_user');
      const u = raw ? JSON.parse(raw) : null;
      setProfilePhoto(loadProfilePhoto(u?.id, photoRole) || '');
    } catch {
      setProfilePhoto('');
    }
  }, []);

  useEffect(() => {
    if (portalRole !== 'admin') return;
    try {
      setAdminProfilePhoto(loadProfilePhoto(null, 'admin') || '');
    } catch {
      setAdminProfilePhoto('');
    }
  }, [portalRole]);

  const persistSession = async (result) => {
    const activeProfilePhoto = role === 'admin' ? adminProfilePhoto : profilePhoto;
    const activeProfilePhotoFile = role === 'admin' ? adminProfilePhotoFile : profilePhotoFile;
    let user = result.user;
    try {
      if (result?.token) {
        const file = activeProfilePhotoFile || (activeProfilePhoto ? await dataUrlToFile(activeProfilePhoto) : null);
        if (file) {
          user = await uploadUserProfilePhoto(file, result.token);
        }
      }
    } catch (_photoErr) {
      if (activeProfilePhoto && result?.user?.id) saveProfilePhoto(result.user.id, activeProfilePhoto);
    }
    user = syncUserProfilePhoto(user || result.user);
    localStorage.setItem('developmentexpress_user', JSON.stringify(user));
    localStorage.setItem('developmentexpress_token', result.token);
    localStorage.setItem('developmentexpress_refresh_token', result.refreshToken);
    // Sync language preference from server
    if (user?.preferred_lang) {
      localStorage.setItem(
        'developmentexpress_lang',
        user.preferred_lang,
      );
    }
    commitPendingProfilePhoto(user?.id, photoRole);
    if (user?.pushNotificationsEnabled) {
      requestPushPermission(result.token);
    }
    navigate('/' + role, { replace: true });
  };

  const normalizePhone = (value) => value.replace(/\D/g, '').slice(-10);

  const handleLogin = async () => {
    if (!identifier || !password) {
      setError(loginUsesPhone ? `${t('mobileNumber')} + ${t('password')}` : `${t('emailAddressLabel')} + ${t('password')}`);
      return;
    }
    if (loginUsesPhone && normalizePhone(identifier).length !== 10) {
      setError(`Valid ${t('mobileNumber')}`);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = { password, role };
      if (loginUsesPhone) {
        payload.phone = normalizePhone(identifier);
      } else {
        payload.email = identifier.trim().toLowerCase();
      }
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      // Admin TOTP step: password OK, full JWT deferred until authenticator code.
      if (response.ok && result?.requires_totp && result?.temp_token && role === 'admin') {
        setTotpTempToken(result.temp_token);
        setTotpPending(true);
        setTotpToken('');
        setLoading(false);
        return;
      }
      if (!response.ok || !result?.user || !result?.token || !result?.refreshToken) {
        const serverError = result?.error;
        if (serverError) {
          setError(serverError);
        } else if (response.status === 403) {
          setError('Account is pending admin approval. Try again after admin activates your account.');
        } else {
          setError(
            loginUsesPhone
              ? 'Invalid mobile number, password, or role. Use 10-digit mobile (no +91), select Client, and the password from registration.'
              : 'Invalid email, password, or role.',
          );
        }
        setLoading(false);
        return;
      }
      await persistSession(result);
    } catch (err) {
      setError(explainApiError(err, API_BASE_URL, t('loginFailedGeneric')));
    }
    setLoading(false);
  };

  const handleVerifyAdminTotp = async () => {
    if (!/^\d{6}$/.test(totpToken.trim())) {
      setError('Enter a valid 6-digit authenticator code');
      return;
    }
    if (!totpTempToken) {
      setError('Session expired. Please sign in again.');
      setTotpPending(false);
      return;
    }
    setTotpLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/admin/totp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          temp_token: totpTempToken,
          totp_token: totpToken.trim(),
        }),
      });
      const result = await response.json();
      if (!response.ok || !result?.user || !result?.token || !result?.refreshToken) {
        setError(result?.error || 'Invalid authenticator code');
        return;
      }
      setTotpPending(false);
      setTotpTempToken('');
      setTotpToken('');
      await persistSession(result);
    } catch (err) {
      setError(explainApiError(err, API_BASE_URL, t('loginFailedGeneric')));
    } finally {
      setTotpLoading(false);
    }
  };

  const handleSendLoginOtp = async () => {
    const phone = normalizePhone(identifier);
    if (phone.length !== 10) {
      setError(`Valid ${t('mobileNumber')}`);
      return;
    }
    setOtpLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login-otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, role }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(result?.error || 'Could not send OTP. Please try again.');
        return;
      }
      setOtpStep('verify');
      setOtp('');
      if (result?.smsConfigured === false) {
        setError(t('loginOtpDevHint'));
      }
    } catch (err) {
      setError(explainApiError(err, API_BASE_URL, 'Could not send OTP. Please try again.'));
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyLoginOtp = async () => {
    const phone = normalizePhone(identifier);
    if (phone.length !== 10) {
      setError(`Valid ${t('mobileNumber')}`);
      return;
    }
    if (!/^\d{6}$/.test(otp.trim())) {
      setError(t('forgotPasswordInvalidOtp'));
      return;
    }
    setOtpLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login-otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, role, otp: otp.trim() }),
      });
      const result = await response.json();
      if (!response.ok || !result?.user || !result?.token || !result?.refreshToken) {
        setError(result?.error || t('loginFailedGeneric'));
        return;
      }
      await persistSession(result);
    } catch (err) {
      setError(explainApiError(err, API_BASE_URL, t('loginFailedGeneric')));
    } finally {
      setOtpLoading(false);
    }
  };

  const switchLoginMode = (mode) => {
    setLoginMode(mode);
    setError('');
    setPassword('');
    setOtp('');
    setOtpStep('request');
    setTotpPending(false);
    setTotpTempToken('');
    setTotpToken('');
  };

  const goToCredentials = () => {
    setError('');
    setLoginMode('password');
    setOtp('');
    setOtpStep('request');
    setTotpPending(false);
    setTotpTempToken('');
    setTotpToken('');
    setStep(2);
  };

  const goBackToRoles = () => {
    setError('');
    setTotpPending(false);
    setTotpTempToken('');
    setTotpToken('');
    setStep(1);
  };

  const cancelTotpStep = () => {
    setTotpPending(false);
    setTotpTempToken('');
    setTotpToken('');
    setError('');
  };

  const portalMeta = portalRole ? PORTAL_META[portalRole] : null;

  return (
    <div className="login-page login-page--ultra relative min-h-dvh overflow-x-hidden bg-[#050d1a] font-sans text-white">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 bg-[linear-gradient(rgba(201,168,76,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(201,168,76,0.03)_1px,transparent_1px)] bg-[size:48px_48px]" aria-hidden />
      <div className="pointer-events-none fixed -right-32 -top-32 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(201,168,76,0.12)_0%,transparent_70%)]" aria-hidden />
      <div className="pointer-events-none fixed -bottom-40 -left-32 h-[480px] w-[480px] rounded-full bg-[radial-gradient(circle,rgba(15,32,64,0.95)_0%,transparent_70%)]" aria-hidden />
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_20%_0%,rgba(201,168,76,0.06),transparent_50%),radial-gradient(ellipse_at_80%_100%,rgba(10,22,40,0.8),transparent_55%)]" aria-hidden />

      <div className="login-lang-bar login-lang-bar--desktop fixed z-50 hidden md:block">
        <LanguageSelector compact={true} />
      </div>

      <div className="login-page__wrapper relative z-10 mx-auto flex min-h-dvh w-full max-w-6xl flex-col px-4 pb-8 pt-[calc(env(safe-area-inset-top,0px)+16px)] md:flex-row md:items-center md:gap-10 md:px-6 md:pb-10 md:pt-8 lg:gap-16 lg:px-8 lg:pb-12 lg:pt-10">
        {/* Hero panel — desktop cinematic side */}
        <aside className="login-hero hidden flex-1 flex-col justify-center md:flex animate-fade-in">
          <div className="mb-8 inline-flex w-fit items-center gap-2 rounded-full border border-de-gold/25 bg-de-gold/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-de-gold/90">
            <Sparkles className="h-3.5 w-3.5" />
            Infrastructure OS
          </div>

          <div className="mb-6 flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-de-logo text-lg font-black text-[#0a1628] shadow-[0_0_40px_rgba(201,168,76,0.35)]">
              DE
            </div>
            <div>
              <h1 className="de-text-gradient text-3xl font-extrabold tracking-tight xl:text-4xl">
                {t('appName')}
              </h1>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.25em] text-de-gold/60">
                {t('tagline')}
              </p>
            </div>
          </div>

          <p className="max-w-md text-lg font-medium leading-relaxed text-white/80 xl:text-xl">
            Smarter machinery booking.
            <span className="text-de-gold"> Simpler operations.</span>
          </p>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-de-muted">
            {t('platform')} — {t('marketingBuiltFor')}
          </p>

          <div className="mt-10 grid max-w-md grid-cols-3 gap-3">
            {trustStats.map((stat) => (
              <div
                key={stat.labelKey}
                className="de-glass rounded-2xl border border-white/10 px-4 py-4 text-center"
              >
                <p className="flex items-center justify-center gap-1 text-xl font-bold text-de-gold">
                  {stat.icon && <stat.icon className="h-4 w-4 fill-de-gold text-de-gold" />}
                  {stat.value}
                </p>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-de-muted">
                  {t(stat.labelKey)}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center gap-2 text-xs text-de-muted">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            Wallet-only policy · Secured · GST compliant
          </div>
        </aside>

        {/* Auth wizard */}
        <main className="mx-auto w-full max-w-[420px] flex-shrink-0 md:mx-0">
          {/* Mobile brand strip */}
          <header className="login-mobile-header login-logo-section login-logo-section--premium mb-6 flex items-start justify-between gap-3 md:hidden">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-de-logo text-sm font-black text-[#0a1628] shadow-[0_0_24px_rgba(201,168,76,0.3)]">
                DE
              </div>
              <div className="min-w-0">
                <p className="de-text-gradient truncate text-base font-extrabold">{t('appName')}</p>
                <p className="truncate text-[9px] font-semibold uppercase tracking-[0.15em] text-de-gold/55">{t('tagline')}</p>
              </div>
            </div>
            <LanguageSelector compact iconOnly />
          </header>

          {/* Step indicator */}
          <div className="mb-5 flex items-center justify-center gap-2 animate-fade-in">
            {[1, 2].map((n) => (
              <div
                key={n}
                className={cn(
                  'h-1.5 rounded-full transition-all duration-500',
                  step === n ? 'w-10 bg-de-gold shadow-[0_0_12px_rgba(201,168,76,0.5)]' : 'w-6 bg-white/10',
                  step > n && 'bg-de-gold/50',
                )}
              />
            ))}
          </div>

          <div className="login-page__card login-page__card--ultra de-glass de-glow-gold overflow-hidden rounded-3xl border border-de-gold/20 bg-gradient-to-br from-[#0f2040]/90 to-[#0a1628]/95 p-6 shadow-[0_32px_80px_rgba(0,0,0,0.55)] sm:p-7">
            <AnimatePresence mode="wait">
              {step === 1 && !portalRole ? (
                <motion.div
                  key="step-role"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                <div className="mb-6">
                  <h2 className="text-2xl font-extrabold tracking-tight text-white sm:text-[1.65rem]">
                    {t('loginIAmQuestion')}
                  </h2>
                  <p className="mt-1.5 text-sm text-de-muted">
                    {t('loginPickRoleHint')}
                  </p>
                </div>

                <div className="login-role-grid login-role-grid--ultra grid grid-cols-2 gap-3" role="radiogroup" aria-label={t('selectRole')}>
                  {ROLES.map((r, idx) => {
                    const Icon = r.icon;
                    const active = role === r.id;
                    return (
                      <motion.button
                        key={r.id}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, delay: idx * 0.06 }}
                        whileTap={{ scale: 0.98 }}
                        className={cn(
                          'login-role-btn login-role-btn--ultra group relative flex min-h-[118px] flex-col items-start rounded-2xl border p-4 text-left transition-colors duration-300',
                          active
                            ? 'border-de-gold/70 bg-de-gold/10 shadow-[0_0_28px_rgba(201,168,76,0.18)] ring-1 ring-de-gold/40'
                            : 'border-white/10 bg-white/[0.03] hover:border-de-gold/35 hover:bg-white/[0.05]',
                        )}
                        onClick={() => {
                          setRole(r.id);
                          setError('');
                          setIdentifier('');
                        }}
                      >
                        <span
                          className={cn(
                            'mb-3 flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-300',
                            active
                              ? 'bg-de-gold/20 text-de-gold'
                              : 'bg-white/5 text-de-muted group-hover:bg-de-gold/10 group-hover:text-de-gold',
                          )}
                        >
                          <Icon className="h-5 w-5" strokeWidth={1.75} />
                        </span>
                        <span className={cn('text-sm font-bold', active ? 'text-de-gold' : 'text-white/90')}>
                          {t(r.labelKey)}
                        </span>
                        <span className="mt-1 line-clamp-2 text-[11px] leading-snug text-de-muted">
                          {t(r.descKey)}
                        </span>
                        {active && (
                          <span className="absolute right-3 top-3 h-2 w-2 rounded-full bg-de-gold shadow-[0_0_8px_rgba(201,168,76,0.8)] animate-pulse-gold" />
                        )}
                      </motion.button>
                    );
                  })}
                </div>

                <div className="login-join-banner mt-4 rounded-2xl border border-de-gold/35 bg-gradient-to-br from-de-gold/12 to-de-gold/5 p-4">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-de-gold/20 text-de-gold">
                      <HardHat className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-white">{t('joinUsBanner')}</p>
                      <p className="mt-1 text-[11px] leading-snug text-de-muted">{t('joinUsHint')}</p>
                      <Link
                        to="/register"
                        className="login-join-btn mt-2.5 inline-flex items-center gap-1 text-sm font-bold text-de-gold transition-colors hover:text-[#e2c97e]"
                      >
                        {t('joinUsAction')}
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>

                <Button
                  type="button"
                  className="login-btn-primary login-btn-primary--ultra mt-5 h-12 w-full rounded-2xl border-0 bg-[length:200%_auto] bg-gradient-to-r from-[#a07830] via-[#e2c97e] to-[#a07830] text-sm font-extrabold uppercase tracking-[0.15em] text-[#0a1628] shadow-[0_12px_32px_rgba(201,168,76,0.32)] transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(201,168,76,0.42)] animate-shimmer"
                  onClick={goToCredentials}
                >
                  {role === 'client' ? 'Continue to Sign In' : 'Continue'}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
                </motion.div>
              ) : (
                <motion.div
                  key="step-login"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                {!portalRole && (
                <button
                  type="button"
                  onClick={goBackToRoles}
                  className="mb-4 flex items-center gap-1.5 text-[12px] font-medium leading-snug text-white/55 transition-colors hover:text-[#C9A84C]"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t('loginChangeRole')}
                </button>
                )}

                {(portalRole === 'client' || portalRole === 'owner' || portalRole === 'operator') && (
                  <div className="mb-6 flex flex-col items-center">
                    <ProfilePhotoBadge
                      value={profilePhoto}
                      onChange={(url, meta) => {
                        setProfilePhoto(url);
                        setProfilePhotoFile(meta?.file || null);
                        setProfilePhotoError(meta?.error || '');
                        if (url) {
                          saveProfilePhoto(null, url, photoRole);
                          if (photoRole === 'owner') {
                            localStorage.removeItem(PHOTO_REMOVED_FLAG_OWNER);
                          }
                        }
                      }}
                      name={identifier || (selectedRole ? t(selectedRole.labelKey) : '')}
                      size={76}
                    />
                    {profilePhoto && (
                      <button
                        type="button"
                        onClick={() => {
                          clearProfilePhoto(null, photoRole);
                          try {
                            const raw = localStorage.getItem('developmentexpress_user');
                            const u = raw ? JSON.parse(raw) : null;
                            if (u?.id) {
                              localStorage.removeItem(`developmentexpress_avatar_${u.id}`);
                            }
                          } catch {
                            /* ignore */
                          }
                          setProfilePhoto('');
                          setProfilePhotoFile(null);
                          if (photoRole === 'owner') {
                            localStorage.setItem(PHOTO_REMOVED_FLAG_OWNER, '1');
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#e94560',
                          fontSize: 11,
                          cursor: 'pointer',
                          marginTop: 4,
                          textDecoration: 'underline',
                        }}
                      >
                        Remove photo
                      </button>
                    )}
                    {profilePhotoError && (
                      <p className="mt-2 text-center text-[11px] text-red-400">{profilePhotoError}</p>
                    )}
                    <h2 className="mt-4 text-xl font-bold text-white">
                      {portalRole === 'client'
                        ? t('loginRoleClientShort')
                        : portalRole === 'owner'
                          ? t('loginRoleOwnerShort')
                          : t('operatorRole')}
                    </h2>
                  </div>
                )}

                {portalRole === 'admin' && (
                  <div className="mb-6 flex flex-col items-center">
                    <ProfilePhotoBadge
                      value={adminProfilePhoto}
                      onChange={(url, meta) => {
                        setAdminProfilePhoto(url);
                        setAdminProfilePhotoFile(meta?.file || null);
                        setAdminProfilePhotoError(meta?.error || '');
                        if (url) {
                          saveProfilePhoto(null, url, 'admin');
                        }
                      }}
                      name={identifier || (selectedRole ? t(selectedRole.labelKey) : '')}
                      size={76}
                    />
                    {adminProfilePhoto && (
                      <button
                        type="button"
                        onClick={() => {
                          clearProfilePhoto(null, 'admin');
                          try {
                            const raw = localStorage.getItem('developmentexpress_user');
                            const u = raw ? JSON.parse(raw) : null;
                            if (u?.id) {
                              localStorage.removeItem(`developmentexpress_avatar_${u.id}`);
                            }
                          } catch {
                            /* ignore */
                          }
                          setAdminProfilePhoto('');
                          setAdminProfilePhotoFile(null);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#e94560',
                          fontSize: 11,
                          cursor: 'pointer',
                          marginTop: 4,
                          textDecoration: 'underline',
                        }}
                      >
                        Remove photo
                      </button>
                    )}
                    {adminProfilePhotoError && (
                      <p className="mt-2 text-center text-[11px] text-red-400">{adminProfilePhotoError}</p>
                    )}
                    <h2 className="mt-4 text-xl font-bold text-white">
                      {t('adminRole')}
                    </h2>
                  </div>
                )}

                {!portalRole && (
                  <div className="mb-6">
                    <h2 className="text-2xl font-extrabold tracking-tight text-white sm:text-[1.65rem]">
                      Let&apos;s sign in
                    </h2>
                    <p className="mt-1.5 text-sm text-de-muted">It&apos;s quick and secure.</p>
                  </div>
                )}

                {selectedRole && !portalRole && (
                  <div className="mb-5 inline-flex items-center gap-2.5 rounded-xl border border-de-gold/25 bg-de-gold/8 px-3 py-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-de-gold/15 text-de-gold">
                      <selectedRole.icon className="h-4 w-4" strokeWidth={1.75} />
                    </span>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-de-muted">{t('loginAs')}</p>
                      <p className="text-sm font-bold text-de-gold">{t(selectedRole.labelKey)}</p>
                    </div>
                  </div>
                )}

                {loginUsesPhone && !totpPending && (
                  <div className="mb-4 grid grid-cols-2 gap-2 rounded-xl bg-white/[0.04] p-1">
                    {['password', 'otp'].map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        className={cn(
                          'rounded-lg py-2.5 text-sm font-semibold transition-all',
                          loginMode === mode
                            ? 'bg-[#C9A84C]/20 text-[#E8D5A3] shadow-sm'
                            : 'text-white/45 hover:text-white/70',
                        )}
                        onClick={() => switchLoginMode(mode)}
                      >
                        {mode === 'password' ? t('loginTabPassword') : t('loginTabOtp')}
                      </button>
                    ))}
                  </div>
                )}

                {totpPending ? (
                  <div className="space-y-3">
                    <div className="rounded-xl border border-de-gold/30 bg-de-gold/10 px-3.5 py-2.5 text-xs text-de-gold">
                      Password verified. Enter your 6-digit authenticator code to finish signing in.
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="login-admin-totp" className="normal-case tracking-normal">
                        Enter 6-digit authenticator code
                        <span className="ml-0.5 text-de-accent">*</span>
                      </Label>
                      <Input
                        id="login-admin-totp"
                        className="login-field-input login-field-input--ultra h-12 rounded-xl tracking-[0.35em]"
                        type="tel"
                        inputMode="numeric"
                        placeholder="000000"
                        maxLength={6}
                        value={totpToken}
                        onChange={(e) => {
                          setTotpToken(e.target.value.replace(/\D/g, '').slice(0, 6));
                          setError('');
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && handleVerifyAdminTotp()}
                        autoComplete="one-time-code"
                        autoFocus
                      />
                    </div>
                  </div>
                ) : (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    {!portalRole && (
                      <Label htmlFor="login-identifier" className="normal-case tracking-normal">
                        {loginUsesPhone ? t('mobileNumber') : t('emailAddressLabel')}
                        <span className="ml-0.5 text-de-accent">*</span>
                      </Label>
                    )}
                    {loginUsesPhone ? (
                      <div className="flex gap-2">
                        <div className="flex h-11 min-w-[88px] items-center justify-center rounded-xl border border-de-gold/30 bg-[rgba(9,17,30,0.55)] text-sm font-semibold text-de-gold">
                          +91
                        </div>
                        <Input
                          id="login-identifier"
                          className="login-field-input login-field-input--ultra h-11 flex-1 rounded-xl text-base sm:text-sm"
                          type="tel"
                          inputMode="numeric"
                          placeholder="9876543210"
                          value={identifier}
                          onChange={(e) => {
                            setIdentifier(e.target.value);
                            setError('');
                            if (loginMode === 'otp') setOtpStep('request');
                          }}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter') return;
                            if (loginMode === 'otp' && otpStep === 'request') handleSendLoginOtp();
                            else if (loginMode === 'password') handleLogin();
                          }}
                          autoComplete="tel"
                        />
                      </div>
                    ) : (
                      <Input
                        id="login-identifier"
                        className="login-field-input login-field-input--ultra h-11 rounded-xl"
                        type="email"
                        inputMode="email"
                        placeholder="your@email.com"
                        value={identifier}
                        onChange={(e) => { setIdentifier(e.target.value); setError(''); }}
                        onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                        autoComplete="email"
                      />
                    )}
                  </div>

                  {loginMode === 'password' && (
                    <div className="space-y-1.5">
                      <div className="relative">
                        <Input
                          id="login-password"
                          className="login-field-input login-field-input--ultra h-12 rounded-xl pr-11"
                          type={showPassword ? 'text' : 'password'}
                          placeholder={portalRole ? 'Password' : 'Your password'}
                          aria-label={t('password')}
                          value={password}
                          onChange={(e) => { setPassword(e.target.value); setError(''); }}
                          onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-de-gold/80 transition-colors hover:bg-de-gold/10 hover:text-de-gold"
                          onClick={() => setShowPassword((prev) => !prev)}
                          aria-label={showPassword ? t('hide') : t('show')}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <div className="flex justify-end pt-0.5">
                        <Link
                          to={`/forgot-password?role=${role}`}
                          className="text-xs font-medium text-[#C9A84C]/80 transition-colors hover:text-[#C9A84C]"
                        >
                          {t('forgotPasswordLink')}
                        </Link>
                      </div>
                    </div>
                  )}

                  {loginUsesPhone && loginMode === 'otp' && (
                    <div className="space-y-2">
                      <p className="text-[11px] leading-snug text-de-muted">{t('loginOtpHint')}</p>
                      {otpStep === 'verify' && (
                        <>
                          <p className="text-xs font-semibold text-de-gold">
                            {t('loginOtpSentTo')} +91 {normalizePhone(identifier)}
                          </p>
                          <Label htmlFor="login-otp" className="normal-case tracking-normal">
                            {t('otpCode')}
                            <span className="ml-0.5 text-de-accent">*</span>
                          </Label>
                          <Input
                            id="login-otp"
                            className="login-field-input login-field-input--ultra h-11 rounded-xl tracking-[0.35em]"
                            type="tel"
                            inputMode="numeric"
                            placeholder="000000"
                            maxLength={6}
                            value={otp}
                            onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                            onKeyDown={(e) => e.key === 'Enter' && handleVerifyLoginOtp()}
                            autoComplete="one-time-code"
                          />
                        </>
                      )}
                    </div>
                  )}
                </div>
                )}

                {error && (
                  <div
                    className={cn(
                      'login-error-box mt-4 rounded-xl px-3.5 py-2.5 text-xs',
                      error === t('loginOtpDevHint')
                        ? 'border border-de-gold/30 bg-de-gold/10 text-de-gold'
                        : 'border border-de-accent/35 bg-de-accent/10 text-de-accent',
                    )}
                    role="alert"
                  >
                    {error}
                  </div>
                )}

                {totpPending ? (
                  <>
                    <Button
                      type="button"
                      className={cn(
                        'mt-5 h-12 w-full rounded-2xl border-0 text-base font-bold text-[#0a1628]',
                        'bg-gradient-to-r from-[#a07830] via-[#e2c97e] to-[#d4b65a]',
                        'shadow-[0_8px_24px_rgba(201,168,76,0.25)] transition-all hover:brightness-105 disabled:opacity-70',
                        !portalRole && 'login-btn-primary login-btn-primary--ultra uppercase tracking-[0.15em] animate-shimmer',
                      )}
                      onClick={handleVerifyAdminTotp}
                      disabled={totpLoading || totpToken.length !== 6}
                    >
                      {totpLoading ? t('signingIn') : (
                        <>
                          Verify &amp; sign in
                          <ShieldCheck className="ml-1 h-4 w-4" />
                        </>
                      )}
                    </Button>
                    <button
                      type="button"
                      className="mt-3 w-full text-center text-xs font-semibold text-de-muted transition-colors hover:text-de-gold"
                      onClick={cancelTotpStep}
                    >
                      Back to password
                    </button>
                  </>
                ) : loginMode === 'password' ? (
                  <Button
                    type="button"
                    className={cn(
                      'mt-5 h-12 w-full rounded-2xl border-0 text-base font-bold text-[#0a1628]',
                      'bg-gradient-to-r from-[#a07830] via-[#e2c97e] to-[#d4b65a]',
                      'shadow-[0_8px_24px_rgba(201,168,76,0.25)] transition-all hover:brightness-105 disabled:opacity-70',
                      !portalRole && 'login-btn-primary login-btn-primary--ultra uppercase tracking-[0.15em] animate-shimmer',
                    )}
                    onClick={handleLogin}
                    disabled={loading}
                  >
                    {loading ? t('signingIn') : (
                      <>
                        {portalRole ? t('loginSignInShort') : t('signIn')}
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    className={cn(
                      'mt-5 h-12 w-full rounded-2xl border-0 text-base font-bold text-[#0a1628]',
                      'bg-gradient-to-r from-[#a07830] via-[#e2c97e] to-[#d4b65a]',
                      'shadow-[0_8px_24px_rgba(201,168,76,0.25)] transition-all hover:brightness-105 disabled:opacity-70',
                      !portalRole && 'login-btn-primary login-btn-primary--ultra uppercase tracking-[0.15em] animate-shimmer',
                    )}
                    onClick={otpStep === 'request' ? handleSendLoginOtp : handleVerifyLoginOtp}
                    disabled={otpLoading}
                  >
                    {otpLoading
                      ? (otpStep === 'request' ? t('loginSendingOtp') : t('loginVerifyingOtp'))
                      : (otpStep === 'request' ? t('loginSendOtp') : t('loginVerifyOtp'))}
                  </Button>
                )}

                {loginUsesPhone && loginMode === 'otp' && otpStep === 'verify' && !totpPending && (
                  <button
                    type="button"
                    className="mt-3 w-full text-center text-xs font-semibold text-de-muted transition-colors hover:text-de-gold"
                    onClick={() => { setOtpStep('request'); setOtp(''); setError(''); }}
                  >
                    {t('loginSendOtp')}
                  </button>
                )}

                {(portalRole === 'client' || portalRole === 'owner') && !totpPending && (
                  <p className="mt-5 text-center text-[12px] leading-snug text-white/50">
                    <Link
                      to={portalMeta?.registerTo || '/register'}
                      className="text-[#C9A84C]/90 hover:text-[#C9A84C]"
                    >
                      {t('loginNewHereRegister')}
                    </Link>
                  </p>
                )}
                {!portalRole && role === 'client' && !totpPending && (
                  <p className="mt-4 text-center text-xs text-de-muted">
                    Don&apos;t have an account?{' '}
                    <Link
                      to="/register/client"
                      className="font-bold text-de-gold transition-colors hover:text-[#e2c97e]"
                    >
                      {t('registerAction')}
                    </Link>
                  </p>
                )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <p className="mt-5 text-center text-[10px] tracking-wide text-de-gold/30">
            © 2026 Development Express
            <a href="/grievance" style={{ color: '#8896a8', fontSize: '11px', marginLeft: '12px', textDecoration: 'none' }}>Grievance</a>
          </p>
        </main>
      </div>
    </div>
  );
};

export function ClientPortalLogin() {
  return <Login fixedPortal="client" />;
}

export function OwnerPortalLogin() {
  return <Login fixedPortal="owner" />;
}

export function OperatorPortalLogin() {
  return <Login fixedPortal="operator" />;
}

export function AdminPortalLogin() {
  return <Login fixedPortal="admin" />;
}

export default Login;
